import { useEffect, useRef, useState, type FormEvent } from 'react';
import { getErrorMessage } from '../../api/apiClient';
import {
  createInvitation,
  deleteInvitation,
  getInvitations,
  getUsers,
  revokeInvitation,
  updateUserStatus,
} from '../../api/usersApi';
import { useAuth } from '../../auth/AuthContext';
import { getRoleLabel, ROLE_LABELS } from '../../auth/roles';
import {
  INVITATION_STATUSES,
  type Invitation,
  type User,
  type UserRole,
} from '../../types/user';
import './Dashboard.css';

type InvitationMutation = 'revoke' | 'delete';

function UserAdmin() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('CLIENT');
  const [submitting, setSubmitting] = useState(false);
  const [invitationMutations, setInvitationMutations] = useState<Record<string, InvitationMutation>>({});
  const mutatingInvitationIds = useRef(new Set<string>());

  useEffect(() => {
    let active = true;

    Promise.all([getUsers(), getInvitations()])
      .then(([userResponse, invitationResponse]) => {
        if (active) {
          setUsers(userResponse.items || []);
          setInvites(invitationResponse.items || []);
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(getErrorMessage(requestError, 'Users and invitations could not be loaded.'));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const invitation = await createInvitation({ email: email.trim(), role });
      setInvites((current) => [invitation, ...current]);
      setEmail('');
      setRole('CLIENT');
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The invitation could not be created.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (user: User) => {
    setError('');

    try {
      const updatedUser = await updateUserStatus(user.id, !user.active);
      setUsers((current) => current.map((item) => item.id === updatedUser.id ? updatedUser : item));
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The user status could not be updated.'));
    }
  };

  const beginInvitationMutation = (invitationId: string, mutation: InvitationMutation): boolean => {
    if (mutatingInvitationIds.current.has(invitationId)) {
      return false;
    }

    mutatingInvitationIds.current.add(invitationId);
    setInvitationMutations((current) => ({ ...current, [invitationId]: mutation }));
    return true;
  };

  const finishInvitationMutation = (invitationId: string) => {
    mutatingInvitationIds.current.delete(invitationId);
    setInvitationMutations((current) => {
      const next = { ...current };
      delete next[invitationId];
      return next;
    });
  };

  const handleRevokeInvitation = async (invitation: Invitation) => {
    if (mutatingInvitationIds.current.has(invitation.id)) {
      return;
    }

    const confirmed = window.confirm(
      `Revoke the invitation for ${invitation.email}? Their current invitation link will stop working immediately.`,
    );
    if (!confirmed || !beginInvitationMutation(invitation.id, 'revoke')) {
      return;
    }

    setError('');

    try {
      const updatedInvitation = await revokeInvitation(invitation.id);
      setInvites((current) => current.map((item) => (
        item.id === updatedInvitation.id ? updatedInvitation : item
      )));
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The invitation could not be revoked.'));
    } finally {
      finishInvitationMutation(invitation.id);
    }
  };

  const handleDeleteInvitation = async (invitation: Invitation) => {
    if (mutatingInvitationIds.current.has(invitation.id)) {
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete the invitation for ${invitation.email}? This action cannot be undone.`,
    );
    if (!confirmed || !beginInvitationMutation(invitation.id, 'delete')) {
      return;
    }

    setError('');

    try {
      await deleteInvitation(invitation.id);
      setInvites((current) => current.filter((item) => item.id !== invitation.id));
    } catch (requestError: unknown) {
      setError(getErrorMessage(requestError, 'The invitation could not be deleted.'));
    } finally {
      finishInvitationMutation(invitation.id);
    }
  };

  if (loading || !currentUser) {
    return <div className="dashboard-route-status" role="status">Loading users...</div>;
  }

  return (
    <section className="dashboard-page">
      <div className="dashboard-page-header">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Users and invitations</h1>
        </div>
      </div>

      {error && <p className="dashboard-error" role="alert">{error}</p>}

      <form className="invite-form dashboard-panel" onSubmit={handleInvite}>
        <div>
          <label htmlFor="invite-email">Email</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="invite-role">Role</label>
          <select id="invite-role" value={role} onChange={(event) => setRole(event.target.value as UserRole)}>
            {Object.entries(ROLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <button className="dashboard-action" type="submit" disabled={submitting}>
          {submitting ? 'Sending...' : 'Invite user'}
        </button>
      </form>

      <div className="admin-grid">
        <article className="dashboard-panel">
          <h2>Accounts</h2>
          <div className="stack-list">
            {users.map((user) => (
              <div key={user.id} className="stack-list-row admin-user-row">
                <span>
                  <strong>{user.name}</strong>
                  <small>{user.email} | {getRoleLabel(user.role)}</small>
                </span>
                <button
                  type="button"
                  className="text-button"
                  onClick={() => handleStatusChange(user)}
                  disabled={user.id === currentUser.id}
                >
                  {user.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            ))}
            {users.length === 0 && <p className="inline-empty-state">No accounts found.</p>}
          </div>
        </article>

        <article className="dashboard-panel">
          <h2>Invitations</h2>
          <div className="stack-list">
            {invites.map((invite) => (
              <div key={invite.id} className="stack-list-row invitation-row">
                <span>
                  <strong>{invite.email}</strong>
                  <small>{getRoleLabel(invite.role)} | Expires {invite.expiresAt}</small>
                </span>
                <div className="invitation-row-controls">
                  <span>{invite.status}</span>
                  {invite.status === INVITATION_STATUSES.PENDING && (
                    <button
                      type="button"
                      className="text-button invitation-revoke"
                      onClick={() => handleRevokeInvitation(invite)}
                      disabled={Boolean(invitationMutations[invite.id])}
                      aria-label={`Revoke invitation for ${invite.email}`}
                    >
                      {invitationMutations[invite.id] === 'revoke' ? 'Revoking...' : 'Revoke'}
                    </button>
                  )}
                  {(invite.status === INVITATION_STATUSES.EXPIRED
                    || invite.status === INVITATION_STATUSES.REVOKED) && (
                    <button
                      type="button"
                      className="text-button invitation-delete"
                      onClick={() => handleDeleteInvitation(invite)}
                      disabled={Boolean(invitationMutations[invite.id])}
                      aria-label={`Permanently delete invitation for ${invite.email}`}
                    >
                      {invitationMutations[invite.id] === 'delete' ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {invites.length === 0 && <p className="inline-empty-state">No invitations found.</p>}
          </div>
        </article>
      </div>
    </section>
  );
}

export default UserAdmin;
